import { useState, useCallback } from 'react';

/**
 * Hook for managing suggestions state and behavior
 * @param {Object} options - Configuration options
 * @param {Array} options.customSuggestions - Custom suggestion list
 * @param {boolean} options.disableAutocomplete - Whether to disable autocomplete
 * @param {Function} options.getSuggestions - Function to fetch suggestions
 * @returns {Object} Suggestions state and handlers
 */
const useSuggestions = ({ customSuggestions = [], disableAutocomplete = false, getSuggestions }) => {
  const [suggestions, setSuggestions] = useState([]);
  const [isSuggestionsOpen, setIsSuggestionsOpen] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(0);

  // Update suggestions based on input text
  const updateSuggestions = useCallback(
    (text, isInputMode) => {
      if (!text || disableAutocomplete) {
        setSuggestions([]);
        setIsSuggestionsOpen(false);
        return;
      }

      const textWithoutSpaces = text.replace(/^\s+/, '').toLowerCase();

      if (customSuggestions.length > 0 && textWithoutSpaces.length > 0) {
        const filtered = customSuggestions.filter(
          (suggestion) => suggestion.text.toLowerCase().indexOf(textWithoutSpaces) !== -1
        );
        const suggestionsWithInput = text ? [{ text }, ...filtered] : filtered;
        setSuggestions(suggestionsWithInput);
        setIsSuggestionsOpen(true);
      } else if (getSuggestions) {
        getSuggestions(text).then((fetchedSuggestions) => {
          const suggestionsWithInput = text ? [{ text }, ...(fetchedSuggestions || [])] : fetchedSuggestions || [];
          setSuggestions(suggestionsWithInput);
          setIsSuggestionsOpen(suggestionsWithInput.length > 0 && isInputMode);
        });
      } else {
        const suggestionsWithInput = text ? [{ text }] : [];
        setSuggestions(suggestionsWithInput);
        setIsSuggestionsOpen(!!text && isInputMode);
      }
    },
    [customSuggestions, disableAutocomplete, getSuggestions]
  );

  // Handle keyboard navigation in suggestions
  const navigateSuggestions = useCallback(
    (direction, resultsScrollableRef) => {
      if (suggestions.length === 0) return false;

      const delta = direction === 'up' ? -1 : 1;
      const newIndex = (selectedIndex + delta + suggestions.length) % suggestions.length;
      setSelectedIndex(newIndex);

      // Scroll to selected
      if (resultsScrollableRef?.current) {
        const resultElements = resultsScrollableRef.current.querySelectorAll('.result');
        if (resultElements[newIndex]) {
          resultElements[newIndex].scrollIntoView({ block: 'nearest' });
        }
      }

      return true;
    },
    [suggestions.length, selectedIndex]
  );

  // Reset suggestions state
  const resetSuggestions = useCallback(() => {
    setSuggestions([]);
    setIsSuggestionsOpen(false);
    setSelectedIndex(0);
  }, []);

  return {
    suggestions,
    setSuggestions,
    isSuggestionsOpen,
    setIsSuggestionsOpen,
    selectedIndex,
    setSelectedIndex,
    updateSuggestions,
    navigateSuggestions,
    resetSuggestions,
  };
};

export default useSuggestions;
